import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { superadminApi, type ReleaseNoteSummaryDto } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { ExternalLink, FilePlus2, Megaphone, Plus, RefreshCw, X } from 'lucide-react'

export function errorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

const fmtDate = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

const STATUS_PILL: Record<ReleaseNoteSummaryDto['status'], string> = {
  Draft: 'bg-slate-100 text-slate-600 border-slate-200',
  Published: 'bg-green-50 text-green-700 border-green-200',
}

// ─── Draft from changelog ────────────────────────────────────────────────────

function FromChangelogModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [includeFixes, setIncludeFixes] = useState(false)

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['superadmin-changelog'],
    queryFn: () => superadminApi.getChangelog().then(r => r.data),
  })

  // One row per internal version, newest first, with a count of what's in it.
  const versions = useMemo(() => {
    const byVersion = new Map<string, { version: string; date: string; features: number; improvements: number; fixes: number }>()
    for (const e of entries) {
      const v = byVersion.get(e.version) ?? { version: e.version, date: e.date, features: 0, improvements: 0, fixes: 0 }
      if (e.date > v.date) v.date = e.date
      if (e.type === 'Feature') v.features++
      else if (e.type === 'Improvement') v.improvements++
      else v.fixes++
      byVersion.set(e.version, v)
    }
    return [...byVersion.values()].sort((a, b) => b.date.localeCompare(a.date) || b.version.localeCompare(a.version))
  }, [entries])

  const create = useMutation({
    mutationFn: () => superadminApi.createReleaseNoteFromChangelog([...picked], includeFixes).then(r => r.data),
    onSuccess: note => navigate(`/superadmin/release-notes/${note.id}`),
  })

  const toggle = (v: string) => setPicked(prev => {
    const next = new Set(prev)
    next.has(v) ? next.delete(v) : next.add(v)
    return next
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Draft from the changelog</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          <p className="text-sm text-gray-500">
            Pick the internal versions to start from. Their wording is copied across as a starting point — you'll rewrite it in plain English in the editor.
          </p>
          {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
          <div className="space-y-1.5">
            {versions.map(v => (
              <label
                key={v.version}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                  picked.has(v.version) ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center gap-3">
                  <input type="checkbox" checked={picked.has(v.version)} onChange={() => toggle(v.version)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
                  <span>
                    <span className="text-sm font-medium text-gray-900">v{v.version}</span>
                    <span className="ml-2 text-xs text-gray-400">{fmtDate(v.date)}</span>
                  </span>
                </span>
                <span className="text-xs text-gray-500">
                  {v.features} feature{v.features === 1 ? '' : 's'} · {v.improvements} improvement{v.improvements === 1 ? '' : 's'} · {v.fixes} fix{v.fixes === 1 ? '' : 'es'}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t space-y-3 shrink-0">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={includeFixes} onChange={e => setIncludeFixes(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
            Include fixes (left out by default — not every bug belongs in public notes)
          </label>
          {create.isError && <p className="text-sm text-red-600">{errorMessage(create.error)}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={picked.size === 0 || create.isPending} onClick={() => create.mutate()}>
              {create.isPending ? 'Creating…' : `Create draft${picked.size > 1 ? ` from ${picked.size} versions` : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ReleaseNotesPage() {
  const [showFromChangelog, setShowFromChangelog] = useState(false)
  const [rebuildMsg, setRebuildMsg] = useState<string | null>(null)

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ['superadmin-release-notes'],
    queryFn: () => superadminApi.getReleaseNotes().then(r => r.data),
  })
  const { data: settings } = useQuery({
    queryKey: ['superadmin-release-note-settings'],
    queryFn: () => superadminApi.getReleaseNoteSettings().then(r => r.data),
  })

  const rebuild = useMutation({
    mutationFn: () => superadminApi.rebuildMarketingSite().then(r => r.data),
    onSuccess: r => setRebuildMsg(r.triggered ? 'Rebuild requested — the public site updates in a minute or two.' : 'The rebuild request didn’t go through. Try again, or push a commit to the marketing site.'),
    onError: e => setRebuildMsg(errorMessage(e)),
  })

  const publicUrl = (slug: string) => settings ? `${settings.publicBaseUrl}/whats-new/${slug}/` : null

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Megaphone size={20} className="text-indigo-500" /> Release notes
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            The public “what's new” — plain English, a subset of the internal changelog. Published notes appear on the marketing site.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline" size="sm"
            disabled={rebuild.isPending || !settings?.rebuildConfigured}
            title={settings?.rebuildConfigured ? 'Rebuild the marketing site now' : 'No rebuild hook is configured for this environment (Marketing:BuildHookUrl)'}
            onClick={() => { setRebuildMsg(null); rebuild.mutate() }}
          >
            <RefreshCw size={14} className={`mr-1.5 ${rebuild.isPending ? 'animate-spin' : ''}`} /> Rebuild site
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowFromChangelog(true)}>
            <FilePlus2 size={14} className="mr-1.5" /> Draft from changelog
          </Button>
          <Link to="/superadmin/release-notes/new">
            <Button size="sm"><Plus size={14} className="mr-1.5" /> New release note</Button>
          </Link>
        </div>
      </div>

      {rebuildMsg && <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{rebuildMsg}</p>}
      {settings && !settings.rebuildConfigured && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          No site rebuild hook is set for this environment, so publishing won't update the marketing site by itself — push any commit to its repo to rebuild it.
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : notes.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Megaphone size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No release notes yet.</p>
          <p className="text-xs text-gray-400 mt-1">Start one from the changelog, or write it from scratch.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
          {notes.map(n => (
            <div key={n.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
              <Link to={`/superadmin/release-notes/${n.id}`} className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_PILL[n.status]}`}>{n.status}</span>
                  <span className="font-medium text-gray-900">{n.title}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {fmtDate(n.date)} · {n.itemCount} item{n.itemCount === 1 ? '' : 's'}
                  {n.sourceVersions && <span className="text-gray-400"> · from v{n.sourceVersions.replace(/, /g, ', v')}</span>}
                </p>
                {n.summary && <p className="text-sm text-gray-500 mt-1 line-clamp-1">{n.summary}</p>}
              </Link>
              {n.status === 'Published' && publicUrl(n.slug) && (
                <a href={publicUrl(n.slug)!} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 shrink-0">
                  <ExternalLink size={12} /> View
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {showFromChangelog && <FromChangelogModal onClose={() => setShowFromChangelog(false)} />}
    </div>
  )
}
